import { useEffect } from "react";
import { useTheme } from "@/context/ThemeContext";

const sections = [
  {
    title: "1. Who we are",
    content: `Siveli ("we," "us," "our") is a Process Physics consultancy that builds tools for HR functions, including Mitti, a multilingual HR policy intelligence assistant deployed for our client companies. This policy explains what data we collect, how we use it, and how it's protected — across our web application, admin panel, and messaging channels including WhatsApp.`,
  },
  {
    title: "2. What data we collect",
    table: {
      headers: ["Data type", "How it's collected"],
      rows: [
        ["Name, employee ID, department, email, phone number", "Uploaded by your employer's HR team to set up your account"],
        ["Policy questions and conversation text", "What you type or say to Mitti, via web or WhatsApp"],
        ["Voice audio", "Processed only to convert speech to text — never stored"],
        ["Leave requests", "Type, dates, reason, and approval status when you use Mitti to apply for leave"],
      ],
    },
  },
  {
    title: "3. How we use this data",
    list: [
      "To answer your HR policy questions from your employer's own policy documents",
      "To process leave applications, cancellations, and status checks",
      "To route approvals to your manager",
      "To maintain an audit log for your employer's HR compliance needs",
      "To identify policy gaps your employer's HR team should address",
    ],
    afterList: "We do not use your data for advertising, and we do not sell your data to any third party.",
  },
  {
    title: "4. WhatsApp-specific handling",
    content: "If your employer enables Mitti on WhatsApp, your phone number is used solely to identify you as a registered employee and to deliver Mitti's replies. Messages are sent and received via Meta's WhatsApp Business Platform. In line with Meta's WhatsApp Business Solution Terms, your conversation data is not used by us or by Meta to train any AI or machine learning model.",
  },
  {
    title: "5. Third-party processors",
    intro: "We use the following service providers to operate Mitti. Each processes data only as needed to provide their specific service, under their own data protection terms:",
    table: {
      headers: ["Provider", "Purpose"],
      rows: [
        ["Google Cloud (Vertex AI)", "Language model for answering policy questions — enterprise tier, no training on your data"],
        ["Supabase", "Secure database for employee records, leave requests, and audit logs"],
        ["Cloudflare", "Secure API routing and request handling"],
        ["Resend", "Sends email notifications for leave approvals"],
        ["Meta (WhatsApp Business Platform)", "Message delivery, if your employer has enabled WhatsApp"],
      ],
    },
  },
  {
    title: "6. Data retention",
    list: [
      "Conversation logs are retained for 90 days by default, then automatically deleted, unless your employer's contract specifies otherwise",
      "Voice audio is never retained — it's discarded immediately after transcription",
      "Leave records are retained for the duration of your employment with your employer, per standard HR record-keeping practice",
    ],
  },
  {
    title: "7. Your data is isolated to your employer",
    content: "Each client company using Mitti has a completely separate, isolated instance. Your data is never visible to, or processed alongside, another company's data.",
  },
  {
    title: "8. Security",
    content: "All data is encrypted in transit and at rest. No credentials or API keys are exposed in any client-facing application. Access to administrative functions is protected and logged. Full technical detail is available in our Security & Data Handling reference document, available on request.",
  },
  {
    title: "9. Your rights",
    content: "You can ask your employer's HR team to review, correct, or request deletion of your data held within Mitti. Because Mitti is deployed by your employer for internal HR purposes, requests are handled through your employer, who is the data controller; Siveli acts as the data processor on their behalf.",
  },
  {
    title: "10. Changes to this policy",
    content: "We may update this policy as Mitti's capabilities evolve. Material changes will be reflected here with an updated effective date.",
  },
  {
    title: "11. Contact",
    html: 'Questions about this policy can be directed to <a href="mailto:rebuild@siveli.com" style="color:var(--sv-gold);text-decoration:underline">rebuild@siveli.com</a>.',
  },
];

export default function PrivacyPage() {
  const { theme } = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--sv-bg-primary)" }}>
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
        {/* Header */}
        <h1
          className="text-3xl sm:text-4xl font-semibold tracking-tight mb-2"
          style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}
        >
          Privacy Policy
        </h1>
        <div className="flex gap-6 mt-4 mb-12">
          <p className="text-sm" style={{ fontFamily: "var(--sv-mono-font)", color: "var(--sv-text-muted)" }}>
            Effective date: 24 July 2026
          </p>
          <p className="text-sm" style={{ fontFamily: "var(--sv-mono-font)", color: "var(--sv-text-muted)" }}>
            Last updated: 24 July 2026
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-10">
          {sections.map((section) => (
            <div key={section.title}>
              <h2
                className="text-xl font-semibold mb-4"
                style={{ fontFamily: "var(--sv-heading-font)", color: "var(--sv-text-primary)" }}
              >
                {section.title}
              </h2>

              {section.content && (
                <p className="text-sm leading-relaxed" style={{ color: "var(--sv-text-body)" }}>
                  {section.content}
                </p>
              )}

              {section.intro && (
                <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--sv-text-body)" }}>
                  {section.intro}
                </p>
              )}

              {section.html && (
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "var(--sv-text-body)" }}
                  dangerouslySetInnerHTML={{ __html: section.html }}
                />
              )}

              {section.list && (
                <ul className="space-y-2 mb-3">
                  {section.list.map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed" style={{ color: "var(--sv-text-body)" }}>
                      <span className="accent-dot mt-1.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}

              {section.afterList && (
                <p className="text-sm leading-relaxed mt-3" style={{ color: "var(--sv-text-body)" }}>
                  {section.afterList}
                </p>
              )}

              {section.table && (
                <div className="overflow-x-auto mt-3">
                  <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {section.table.headers.map((h) => (
                          <th
                            key={h}
                            className="text-left py-2 px-3 text-xs tracking-wider uppercase"
                            style={{
                              fontFamily: "var(--sv-mono-font)",
                              color: "var(--sv-gold)",
                              borderBottom: "1px solid var(--sv-border)",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.rows.map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td
                              key={j}
                              className="py-2.5 px-3"
                              style={{
                                color: j === 0 ? "var(--sv-text-primary)" : "var(--sv-text-muted)",
                                borderBottom: "1px solid var(--sv-border)",
                                fontWeight: j === 0 ? 500 : 400,
                              }}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Back link */}
        <div className="mt-16 pt-8" style={{ borderTop: "1px solid var(--sv-border)" }}>
          <a href="/" className="text-sm hover:underline" style={{ color: "var(--sv-gold)" }}>
            &larr; Back to siveli.com
          </a>
        </div>
      </div>
    </div>
  );
}
